import React from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Typography, Space, Avatar } from 'antd';
import { ArrowRightOutlined, UserSwitchOutlined } from '@ant-design/icons';

import TrackerService from '../../utils/TrackerService';
import { useClientRoles } from '../../models/useClientRoles';
import { useClientRolesForUser } from '../../models/useClientRolesForUser';
import { getAppBaseUrl } from '../../utils/URLBuilder';

import { useClientDetails } from './useClientDetails';

const { Paragraph } = Typography;

// Home stays the same height whether you run two clients or forty — a strip of
// up to STRIP_LIMIT tiles, then a "+N more" card into the searchable full list.
// Mirrors LoadableTeamsGrid exactly so the two strips read as one system.
export const STRIP_LIMIT = 8;

const STRIP_ITEM = { flex: '0 0 auto', width: 200 };
const TILE_HEIGHT = 200;

/**
 * Switching client = FULL navigation, never an SPA push: AccountProfileScreen
 * reads its clientId in the constructor (componentDidUpdate is empty), so a
 * param-only route change renders NOTHING new. The Switch User button has always
 * navigated via location.assign for exactly this reason — mirror it.
 */
export function navigateToClient(targetClientId) {
  // Surface-aware, like LoadableTeamsGrid's getTeamsUrl: on the admin surface a
  // client opens as its ADMIN page (the admin walks the delegation graph as
  // admin — seeing what the client sees, never becoming them).
  const isAdminSurface = window.location.pathname.startsWith('/admin/');
  const base = isAdminSurface ? '/admin/clients' : '/client/manage';
  window.location.assign(`${getAppBaseUrl()}${base}/${targetClientId}`);
}

const isLocalHost = window.location.hostname === 'localhost'
  || window.location.hostname === '127.0.0.1';

export function trackDelegatedSwitch(clientId, targetClientId) {
  // Same event the Switch User list fires, so funnels line up; source
  // distinguishes the grid from the old switcher.
  const tracker = new TrackerService({ isTest: isLocalHost });
  const data = { clientId, targetClientId, source: 'delegated_grid' };
  tracker.mixpanelTrack('client_clicked_switch_roles', data);
  if (!isLocalHost) {
    tracker.googleTrack('client_clicked_switch_roles', data);
  }
}

/** One delegated client as a tile: avatar, name, open. */
export const DelegatedClientTile = ({ id, currentClientId, onOpen }) => {
  const client = useClientDetails(id);
  if (!client) return null;

  return (
    <Card
      hoverable
      data-testid={`delegated-tile-${id}`}
      onClick={() => onOpen(id)}
      style={{ height: TILE_HEIGHT, borderRadius: 16 }}
      bodyStyle={{
        height: '100%', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16,
      }}
    >
      {client.avatar
        ? <img src={client.avatar} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
        : (
          // Same 56px footprint as the photo, initialled with the CLIENT's name
          // (FallbackAvatar shows the VIEWER's initial — wrong identity here).
          <Avatar size={56} style={{ backgroundColor: '#7265e6', fontSize: 24 }}>
            {`${client.displayName || id}`.charAt(0).toUpperCase()}
          </Avatar>
        )}
      <Paragraph
        title={client.displayName || id}
        style={{ margin: 0, fontWeight: 600, textAlign: 'center', width: '100%' }}
        ellipsis={{ rows: 1 }}
      >
        {client.displayName || id}
      </Paragraph>
      <Space size={4} style={{ fontSize: 12, color: '#888' }}>
        <span>Open</span>
        <ArrowRightOutlined />
      </Space>
    </Card>
  );
};

/**
 * Delegated clients as first-class home objects: the accounts this person runs
 * for others, presented like the teams strip instead of hiding behind the
 * "Switch User" button. Renders nothing at all when there are no delegations.
 */
export const DelegatedClientsGrid = ({ clientId, isAdmin = false, ownerUid = null }) => {
  const history = useHistory();
  // Two data sources, one grid: the client surface derives delegations from the
  // SIGNED-IN user; the admin surface asks about the VIEWED client's owner
  // (useClientRoles would answer with the admin's own — empty — list).
  const ownRoles = useClientRoles(isAdmin ? null : clientId);
  const ownerRoles = useClientRolesForUser(isAdmin ? ownerUid : null);
  const roles = isAdmin ? ownerRoles : ownRoles;

  // Own client carries isLoggedInClient — the strip shows only the OTHERS.
  const delegated = (roles || []).filter((r) => r && r.id && !r.isLoggedInClient);
  if (delegated.length === 0) return null;

  const allDelegatedUrl = isAdmin
    ? `/admin/clients/${clientId}/delegated`
    : `/client/manage/${clientId}/delegated`;

  const openClient = (targetClientId) => {
    trackDelegatedSwitch(clientId, targetClientId);
    navigateToClient(targetClientId);
  };

  return (
    <div data-testid="delegated-clients-grid">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingTop: 30 }}>
        <Paragraph style={{ margin: 0, flex: 1, fontWeight: 'bold' }}>
          <UserSwitchOutlined /> {isAdmin ? 'Client Access — This Owner' : 'Client Access'} ({delegated.length})
        </Paragraph>
        <button
          type="button"
          className="btn btn-outline-dark btn-sm"
          style={{ marginLeft: 10, fontSize: 12, padding: '4px 8px' }}
          onClick={() => history.push(allDelegatedUrl)}
        >
          Access list
        </button>
      </div>
      <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8 }}>
        {delegated.slice(0, STRIP_LIMIT).map((role) => (
          <div key={`delegated-${role.id}`} style={STRIP_ITEM}>
            <DelegatedClientTile id={role.id} currentClientId={clientId} onOpen={openClient} />
          </div>
        ))}
        {delegated.length > STRIP_LIMIT && (
          <div key="delegated-more" style={STRIP_ITEM}>
            <Card
              hoverable
              data-testid="delegated-see-all"
              onClick={() => history.push(allDelegatedUrl)}
              style={{ height: TILE_HEIGHT, borderRadius: 16, borderStyle: 'dashed' }}
              bodyStyle={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <Paragraph style={{ margin: 0, fontWeight: 600 }}>
                +{delegated.length - STRIP_LIMIT} more
              </Paragraph>
              <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>Access list</Paragraph>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default DelegatedClientsGrid;
